import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n";
import type { SessionRecord, SessionStatus } from "../types/api";

interface Props {
  selectedConversationId: string | null;
  // Bumped by the parent whenever a turn is created/finishes so the list refetches.
  version: number;
  onSelect: (conversationId: string) => void;
}

interface ConversationSummary {
  id: string;
  title: string;
  time: string;
  status: SessionStatus;
  earliest: number;
  latest: number;
}

function titleOf(s: SessionRecord): string {
  const raw = (s.promptPreview || "").split("\n").find((l) => l.trim().length > 0) ?? "";
  return raw.trim() || "—";
}

// Group individual turns (sessions) into conversations: title from the first
// turn, time/status from the most recent. Sessions arrive newest-first.
function groupConversations(sessions: SessionRecord[]): ConversationSummary[] {
  const map = new Map<string, ConversationSummary>();
  for (const s of sessions) {
    const cid = s.conversationId ?? s.id;
    if (cid.startsWith("research_")) continue;
    const created = Date.parse(s.createdAt) || 0;
    const existing = map.get(cid);
    if (!existing) {
      map.set(cid, { id: cid, title: titleOf(s), time: s.createdAt, status: s.status, earliest: created, latest: created });
      continue;
    }
    if (created > existing.latest) {
      existing.latest = created;
      existing.time = s.createdAt;
      existing.status = s.status;
    }
    if (created < existing.earliest) {
      existing.earliest = created;
      existing.title = titleOf(s);
    }
  }
  return [...map.values()].sort((a, b) => b.latest - a.latest);
}

function relativeTime(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const min = 60_000;
  const hr = 3_600_000;
  const day = 86_400_000;
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  if (abs < hr) return rtf.format(Math.round(diff / min), "minute");
  if (abs < day) return rtf.format(Math.round(diff / hr), "hour");
  if (abs < 7 * day) return rtf.format(Math.round(diff / day), "day");
  return d.toLocaleDateString(lang, { month: "short", day: "numeric" });
}

// The cloud conversation history, rendered inline inside the Sidebar.
export function ConversationList({ selectedConversationId, version, onSelect }: Props) {
  const { client, account } = useAuth();
  const { t, lang } = useI18n();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedAccountIdRef = useRef<string | null>(null);

  // Refetch when the account or parent version changes. Account switching can
  // leave the previous request in flight; ignore that stale response so it can
  // never replace the personal conversation list after returning from a
  // workspace.
  useEffect(() => {
    let cancelled = false;
    if (!client || !account) {
      loadedAccountIdRef.current = null;
      setConversations([]);
      setLoading(false);
      return () => { cancelled = true; };
    }
    const switchingAccount = loadedAccountIdRef.current !== account.id;
    if (switchingAccount) {
      setConversations([]);
      setLoading(true);
    }
    void (async () => {
      try {
        const { sessions } = await client.listSessions(account.id);
        if (!cancelled) {
          loadedAccountIdRef.current = account.id;
          setConversations(groupConversations(sessions));
        }
      } catch {
        // Non-fatal: the list just stays as-is; the chat pane surfaces real errors.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [version, client, account]);

  if (loading) {
    return (
      <div className="center" style={{ height: 80 }}>
        <div className="spinner" />
      </div>
    );
  }
  if (conversations.length === 0) {
    return <p className="muted history-empty">{t("history.empty")}</p>;
  }
  return (
    <>
      {conversations.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`history-item ${c.id === selectedConversationId ? "active" : ""}`}
          onClick={() => onSelect(c.id)}
          title={c.title}
        >
          <span className="h-title">{c.title}</span>
          <span className="h-meta">
            <span className={`h-dot ${c.status}`} />
            <span className="h-time">{relativeTime(c.time, lang)}</span>
          </span>
        </button>
      ))}
    </>
  );
}
